import {
  getAllIds,
  getCountByStatus,
  useGetAllExhibitions,
} from "@/api/exhibition/exhibition";
import NewExhibitionDialog from "@/components/exhibition/NewExhibitionDialog";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DashboardListGridLayout } from "@/components/dashboard/DashboardListGridLayout";
import { GridCardWithActions } from "@/components/links/GridCardWithActions";
import { ExhibitionLinkGridCard } from "@/components/links/relatedLinkGridCards";
import { DashboardGridCardActions } from "@/components/table/DashboardGridCardActions";
import { DataTable } from "@/components/ui/datatable";
import {
  exhibitionAdvancedSearchConfig,
  exhibitionColumns,
} from "@/config/dashboard/exhibition";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import { useDashboardListScrollContext } from "@/hooks/useDashboardListScrollContext";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Exhibition } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";

const STORAGE_KEY = "exhibition-table-preferences";
const VIGNETTE_KEY = "expositionsDescriptionVignette";

const flatVisibleExhibitionColumns = [
  ...exhibitionColumns.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const ensureVignetteSecond = (order: string[]): string[] => {
  const without = order.filter((k) => k !== VIGNETTE_KEY);
  if (without.length === 0) return order;
  return [without[0], VIGNETTE_KEY, ...without.slice(1)];
};

const ExhibitionDashboard = () => {
  const { t } = useTranslation();

  const {
    countByStatus,
    currentCount: totalCount,
    pagination,
    setPagination,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Exhibition>({
    resourceName: "exhibition",
    resourceCounter: getCountByStatus,
    statusField: "expositionsPublicationStatus",
  });

  const { data, isFetching } = useGetAllExhibitions(fetchParams);
  const {
    listKey,
    navigateToFiche,
    entityIds,
    isLoading: listScrollLoading,
    listPagination,
  } = useDashboardListScrollContext(data, pagination, isFetching, STORAGE_KEY);

  const actionsColumn = useDashboardActionsColumn<Exhibition>({
    documentType: "exhibition",
    publicationStatusField: "expositionsPublicationStatus",
    onEdit: (row) =>
      navigateToFiche(`/edit/exhibition/${row.id}`, row.id),
  });

  const columns: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [...generateTable(exhibitionColumns, "exhibition"), actionsColumn];
  }, [actionsColumn]);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    const order = saved
      ? saved.order || flatVisibleExhibitionColumns
      : flatVisibleExhibitionColumns;
    return ensureVignetteSecond(ensureActionsColumnLast(order));
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...exhibitionColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...exhibitionColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
        [ACTIONS_COLUMN_ID]: true,
      };
    },
  );

  const {
    selectionState,
    setSelectionState,
    dataTableSelection,
    isSelected,
    setRowSelected,
  } = useDashboardRowSelection<Exhibition>({
      getAllRowIds: () => getAllIds(fetchParams.where),
    });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">
          {t("dashboardTitleExhibitions")}
        </p>
        <GlobalSearch resource="exhibitions"
          config={exhibitionAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={exhibitionAdvancedSearchConfig}
            resource="exhibitions"
          />
          <AdvancedSort config={exhibitionAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createEexhibition")}
            Dialog={NewExhibitionDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DashboardListGridLayout
          storageKey={STORAGE_KEY}
          listKey={listKey}
          entityIds={entityIds}
          isLoading={listScrollLoading}
          listPagination={listPagination}
          setListPagination={setPagination}
          listTable={
            <DataTable
              onRowClick={(row) =>
                navigateToFiche(`/edit/exhibition/${row.original.id}`, row.original.id)
              }
              getListItemId={(row) => row.original.id}
              pagination={{
                pagination: pagination,
                setPage: setPagination,
                totalSize: totalCount,
              }}
              columns={columns}
              data={data ?? []}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
              }}
              selection={dataTableSelection}
            />
          }
          gridContent={
            <>
              {(data ?? []).map((item) => (
                <GridCardWithActions
                  key={item.id}
                  selected={isSelected(item.id)}
                  onSelectedChange={(checked) =>
                    setRowSelected(item.id, checked)
                  }
                  actions={
                    <DashboardGridCardActions
                      documentType="exhibition"
                      item={item}
                      publicationStatusField="expositionsPublicationStatus"
                      onEdit={(row) =>
                        navigateToFiche(`/edit/exhibition/${row.id}`, row.id)
                      }
                    />
                  }
                >
                  <ExhibitionLinkGridCard
                    item={item}
                    t={t}
                    onTextClick={() =>
                      navigateToFiche(`/edit/exhibition/${item.id}`, item.id)
                    }
                  />
                </GridCardWithActions>
              ))}
            </>
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: totalCount,
          }}
        />
        <DashboardSelectionBar
          documentType="exhibition"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        />
      </div>
    </div>
  );
};

export default ExhibitionDashboard;
